import React, { useRef, useEffect, useMemo, useCallback } from "react";
import { Container, Graphics, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { closePaytable } from "../../../../State/actions";
import { selectPaytableActive } from "../../../../State/selectors";
import anime from "animejs";
import { PixiSettings } from "../../../../pixiSettings";
import { FONT_CONFIG } from "../../../../textureConfigs";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import PaytableSlider from "./PaytableSlider";

const Paytable = ({ toolbar }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const paytableActive = useSelector(selectPaytableActive);
  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
  } = useDimensions();
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  const handleClose = useCallback(() => {
    dispatch(closePaytable());
  }, [dispatch]);

  // Calculate viewport dimensions
  const viewportConfig = useMemo(() => {
    // Get viewport configuration based on device and orientation
    const getViewportConfig = () => {
      // MOBILE PORTRAIT
      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return {
            maxWidth: dimensions.width * 0.75,
            paddingTop: dimensions.height / 5.75,
            paddingBottom: 0,
            titleFontSizeMultiplier: 1.35,
            closeButtonOffsetMultiplier: 1.65,
          };
        } else {
          // MOBILE LANDSCAPE
          return {
            maxWidth: toolbar.width * 0.8,
            paddingTop: dimensions.height / 3.45,
            paddingBottom: 0,
            titleFontSizeMultiplier: 1.3,
            closeButtonOffsetMultiplier: 1.35,
          };
        }
        // TABLET PORTRAIT
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return {
            maxWidth: dimensions.width * 0.75,
            paddingTop: dimensions.height /5.5,
            paddingBottom: 0,
            titleFontSizeMultiplier: 1.45,
            closeButtonOffsetMultiplier: 1.75,
          };
        } else {
          // TABLET LANDSCAPE
          return {
            maxWidth: toolbar.width * 0.65,
            paddingTop: dimensions.height / 4.75,
            paddingBottom: 0,
            titleFontSizeMultiplier: 1.0,
            closeButtonOffsetMultiplier: 1.65,
          };
        }
      } else {
        // DESKTOP
        return {
          maxWidth: toolbar.width * 0.575,
          paddingTop: dimensions.height / 3.25,
          paddingBottom: 0,
          titleFontSizeMultiplier: 1.15,
          closeButtonOffsetMultiplier: 1.85,
        };
      }
    };

    const config = getViewportConfig();
    const viewportHeight =
      dimensions.height - config.paddingTop - config.paddingBottom;

    // Center position
    const viewportX = dimensions.width / 2;
    const viewportY = config.paddingTop;

    return {
      maxWidth: config.maxWidth,
      viewportHeight,
      viewportX,
      viewportY,
      paddingTop: config.paddingTop,
      paddingBottom: config.paddingBottom,
      titleFontSizeMultiplier: config.titleFontSizeMultiplier,
      closeButtonOffsetMultiplier: config.closeButtonOffsetMultiplier,
    };
  }, [dimensions, device, orientation, toolbar.width]);

  // Calculate title font size (needed for positioning calculations)
  const baseTitleFontSize = 56;
  const titleFontSize = useMemo(
    () =>
      Math.round(
        baseTitleFontSize *
          viewportConfig.titleFontSizeMultiplier *
          uiSymbolScale
      ),
    [viewportConfig.titleFontSizeMultiplier, uiSymbolScale]
  );

  // Title text style
  const titleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "bold",
        fill: 0xffffff, // White color
        align: "center",
      }),
    [titleFontSize]
  );

  // Memoize translation text
  const titleText = useMemo(() => t("paytable_title", { ns: "common" }), [t]);

  // Memoize title Y position calculation (used in multiple places)
  const titleY = useMemo(
    () => viewportConfig.viewportY - titleFontSize * 3.65,
    [viewportConfig.viewportY, titleFontSize]
  );

  // Memoize separator line Y position
  const separatorLineY = useMemo(
    () => viewportConfig.viewportY - titleFontSize * 2.0,
    [viewportConfig.viewportY, titleFontSize]
  );

  // Memoize line width calculation
  const lineWidth = useMemo(
    () => viewportConfig.maxWidth + viewportConfig.maxWidth * 0.1,
    [viewportConfig.maxWidth]
  );

  // Memoize separator line X position
  const separatorLineX = useMemo(
    () => Math.round(dimensions.width / 2 - lineWidth / 2),
    [dimensions.width, lineWidth]
  );

  // Memoize viewport container position
  const viewportContainerPosition = useMemo(
    () => ({
      x: Math.round(viewportConfig.viewportX),
      y: Math.round(separatorLineY + 3),
    }),
    [viewportConfig.viewportX, separatorLineY]
  );

  // Memoize title X position (centered)
  const titleX = useMemo(
    () => Math.round(dimensions.width / 2),
    [dimensions.width]
  );

  // Memoize Graphics draw functions
  const drawBackground = useCallback(
    (g) => {
      g.clear();
      g.beginFill(0x000000, 1);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height]
  );

  const drawSeparatorLine = useCallback(
    (g) => {
      g.clear();
      g.beginFill(0xfeff00, 1); // Same yellow color as title
      g.drawRect(0, 0, lineWidth, 3);
      g.endFill();
    },
    [lineWidth]
  );

  // Calculate close button position: right side of viewport, vertically aligned with title
  const closeButtonPosition = useMemo(() => {
    const viewportRightEdge =
      viewportConfig.viewportX + viewportConfig.maxWidth / 2;

    // Center button vertically with the title
    const buttonCenterY = titleY + titleFontSize / 1.5;

    return {
      x: Math.round(viewportRightEdge),
      y: Math.round(buttonCenterY),
    };
  }, [
    viewportConfig.viewportX,
    viewportConfig.maxWidth,
    titleY,
    titleFontSize,
  ]);

  useEffect(() => {
    if (containerRef.current) {
      if (paytableActive) {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          easing: "easeOutQuad",
        });
      } else {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 0,
          duration: 200,
          easing: "easeInQuad",
        });
      }
    }
  }, [paytableActive]);

  return (
    <Container
      ref={containerRef}
      name="secondary_paytable_overlay"
      zIndex={10}
      eventMode={paytableActive ? "static" : "none"}
    >
      <Graphics
        name="secondary_paytable_background"
        draw={drawBackground}
        alpha={0.9}
        eventMode={paytableActive ? "static" : "none"}
        onpointertap={(e) => e.stopPropagation()}
      />

      {/* Title */}
      <Text
        text={titleText}
        style={titleStyle}
        anchor={{ x: 0.5, y: 0 }}
        x={titleX}
        y={Math.round(titleY)}
        resolution={2}
      />

      {/* Title separator line */}
      <Graphics
        name="secondary_paytable_title_line"
        draw={drawSeparatorLine}
        x={separatorLineX}
        y={Math.round(separatorLineY)}
      />

      {/* Viewport container with slider - starts right below the separator line */}
      <Container
        name="secondary_paytable_viewport"
        x={viewportContainerPosition.x}
        y={viewportContainerPosition.y}
      >
        <PaytableSlider viewportConfig={viewportConfig} totalSlides={7} />
      </Container>

      {/* Close Button */}
      <ButtonMenuClose
        onClose={handleClose}
        x={closeButtonPosition.x}
        y={closeButtonPosition.y}
      />
    </Container>
  );
};

export default Paytable;
